import {
  type AllowedMaterialSpec,
  type BuyerMaterialSpecificationAdminListItem,
  type BuyerMaterialSpecificationBuyerListItem,
  isPortOfDischargeRequired,
  MaterialComponentCategory,
  type SimpleTradeCompanyListItem,
} from 'api';
import { convertFromMaterialComponentValue, MaterialSupportedComponentList } from 'features';
import {
  AllowedMaterialSpecAutocomplete,
  BuyerMaterialSpecificationsAutocomplete,
  DateField,
  IncotermSelectField,
  MaterialGradeAutocomplete,
  PortAutocomplete,
  PriceAsCurrencyOrIndex,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanyLeadAutocomplete,
  useFormContext,
  useWatch,
} from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';

import { CompanySourceEnum, type CompanySourceType } from '../../enums';
import type { DemandSignalFormValues } from '../../schema/DemandSignalSchema/DemandSignalSchema';
import { CompanySourceToggleButton } from '../CompanySourceToggleButton/CompanySourceToggleButton';

export const DemandSignalFormSection = () => {
  const { t } = useTranslation('orders');
  const form = useFormContext<DemandSignalFormValues>();
  const { setValue } = form;
  const [incoterm, portOfDischargeId] = useWatch({
    name: ['incoterm', 'port_of_discharge_id'],
  });
  const [selectedCompanyType, setSelectedCompanyType] = useState<CompanySourceType>(CompanySourceEnum.GROUND_CONTROL);
  const [tradeCompany, setTradeCompany] = useState<SimpleTradeCompanyListItem | undefined>();
  const [buyerMaterialSpec, setBuyerMaterialSpec] = useState<
    BuyerMaterialSpecificationAdminListItem | BuyerMaterialSpecificationBuyerListItem | undefined
  >();
  const [materialSpec, setMaterialSpec] = useState<AllowedMaterialSpec | undefined>();

  const onTradeCompanyChange = (tradeCompany: SimpleTradeCompanyListItem) => {
    if (tradeCompany.currency) {
      setValue('currency', tradeCompany.currency);
    }
    if (tradeCompany.preferred_port_of_discharge_id) {
      setValue('port_of_discharge_id', tradeCompany.preferred_port_of_discharge_id);
    }
    setTradeCompany(tradeCompany);
  };

  const onTradeCompanyTypeChange = (tradeCompanyType: CompanySourceType) => {
    setValue('trade_company_id', undefined);
    setValue('trade_company_lead_id', undefined);
    setValue('buyer_material_spec_id', undefined);
    setSelectedCompanyType(tradeCompanyType);
    setTradeCompany(undefined);
    setBuyerMaterialSpec(undefined);
  };

  const onBuyerMaterialSpecChange = (
    buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem | BuyerMaterialSpecificationBuyerListItem
  ) => {
    setBuyerMaterialSpec(buyerMaterialSpec);
    setValue('material_grade_id', buyerMaterialSpec.material_grade.id);
    setValue(
      'material_component_values',
      convertFromMaterialComponentValue(buyerMaterialSpec.material_component_values)
    );
  };

  const onAllowedMaterialSpecChange = (materialSpec: AllowedMaterialSpec) => {
    setMaterialSpec(materialSpec);
  };

  return (
    <>
      <CompanySourceToggleButton
        onTradeCompanyTypeChange={onTradeCompanyTypeChange}
        tradeCompanyType={selectedCompanyType}
      />
      {selectedCompanyType === CompanySourceEnum.GROUND_CONTROL ? (
        <>
          <TradeCompanyAutocomplete
            name="trade_company_id"
            label={t('tradeSignals.demandForm.labels.selectBuyerFromGroundControl')}
            onlyBuyers={true}
            onSelectOption={(tradeCompany) => onTradeCompanyChange(tradeCompany)}
          />
          <BuyerMaterialSpecificationsAutocomplete
            name="buyer_material_spec_id"
            tradeCompanyId={tradeCompany?.id}
            onSelectOption={onBuyerMaterialSpecChange}
          />
        </>
      ) : (
        <TradeCompanyLeadAutocomplete
          name="trade_company_lead_id"
          label={t('tradeSignals.demandForm.labels.selectBuyerFromHubspot')}
        />
      )}
      <AllowedMaterialSpecAutocomplete
        label={t('tradeSignals.common.labels.material')}
        name="material_spec_id"
        required
        onSelectOption={onAllowedMaterialSpecChange}
      />
      <MaterialSupportedComponentList
        name="material_component_values"
        materialId={buyerMaterialSpec?.material.id || materialSpec?.material_id}
        materialColourId={buyerMaterialSpec?.material_colour?.id || materialSpec?.colour_id || undefined}
        category={MaterialComponentCategory.MATERIAL_QUALITY}
        disabled={!!buyerMaterialSpec}
      />
      <MaterialGradeAutocomplete
        label={t('tradeSignals.common.labels.materialGrade')}
        name="material_grade_id"
        required
        disabled={!!buyerMaterialSpec}
      />
      <PriceAsCurrencyOrIndex />
      <IncotermSelectField fullWidth />
      {isPortOfDischargeRequired(incoterm) && (
        <PortAutocomplete
          name="port_of_discharge_id"
          label={t('tradeSignals.demandForm.labels.portOfDischarge')}
          defaultValue={portOfDischargeId}
        />
      )}
      <TextField
        label={t('tradeSignals.common.labels.tonnes')}
        name="tonnes"
        type="number"
        required
        data-testid="tonnes"
      />
      <DateField label={t('tradeSignals.demandForm.labels.latestDeliveryDate')} name="latest_delivery_date" />
      <TextField
        multiline
        label={t('tradeSignals.common.labels.standupNotes')}
        name="notes"
        aria-label={t('tradeSignals.common.labels.notes')}
      />
    </>
  );
};
